import { Routes, Route, useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CartProvider, useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Loader2 } from "lucide-react";
import CartDrawer from "@/pages/storefront/CartDrawer";
import VisionLogo from "@/components/shared/VisionLogo";
import Home from "@/pages/storefront/Home";
import Catalog from "@/pages/storefront/Catalog";
import Product from "@/pages/storefront/Product";
import Checkout from "@/pages/storefront/Checkout";

function StoreHeader({ store }) {
  const { count } = useCart();
  const [openCart, setOpenCart] = useState(false);
  const base = `/store/${store.slug}`;
  return (
    <>
      <header className="sticky top-0 z-40 bg-[#FAF9F6]/90 backdrop-blur border-b border-black/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to={base} className="flex items-center gap-2">
            {store.logo ? <img src={store.logo} alt={store.name} className="h-8" /> : <span className="font-display text-2xl font-bold text-neutral-900">{store.name}</span>}
          </Link>
          <nav className="hidden sm:flex items-center gap-8 text-sm text-neutral-700">
            <Link to={base} className="hover:text-neutral-900">Accueil</Link>
            <Link to={`${base}/catalogue`} className="hover:text-neutral-900" data-testid="storefront-nav-catalog">Catalogue</Link>
          </nav>
          <button onClick={() => setOpenCart(true)} className="relative" data-testid="storefront-cart-btn">
            <ShoppingBag className="text-neutral-900" size={22} />
            {count > 0 && <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full text-xs grid place-items-center text-white mono" style={{ background: store.secondary_color || "#3DBB2C" }}>{count}</span>}
          </button>
        </div>
      </header>
      <CartDrawer open={openCart} onOpenChange={setOpenCart} store={store} />
    </>
  );
}

function StorefrontInner() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get(`/public/store/${slug}`).then((r) => setData(r.data)).catch(() => setError(true)); }, [slug]);

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-[#FAF9F6] text-center p-6">
      <div><h1 className="font-display text-3xl font-bold text-neutral-900 mb-2">Boutique introuvable</h1>
        <Button onClick={() => navigate("/")} className="mt-4" style={{ background: "#0F0F12", color: "#fff" }}>Retour</Button></div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-[#FAF9F6] text-neutral-500"><Loader2 className="animate-spin mr-2" /> Chargement…</div>;

  const store = data.store;
  return (
    <CartProvider slug={slug}>
      <div className="min-h-screen bg-[#FAF9F6]" style={{ "--acc": store.secondary_color || "#3DBB2C" }}>
        <StoreHeader store={store} />
        <Routes>
          <Route index element={<Home data={data} />} />
          <Route path="catalogue" element={<Catalog store={store} categories={data.categories} />} />
          <Route path="produit/:pid" element={<Product store={store} />} />
          <Route path="checkout" element={<Checkout store={store} />} />
        </Routes>
        <footer className="border-t border-black/10 mt-16 py-10">
          <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row justify-between gap-4 text-sm text-neutral-500">
            <span className="font-display text-xl font-bold text-neutral-900">{store.name}</span>
            <div className="space-y-1">
              {store.phone && <p>📞 {store.phone}</p>}
              {store.email && <p>📧 {store.email}</p>}
              {(store.city || store.country) && <p>📍 {store.city} {store.country}</p>}
            </div>
            <span className="flex items-center gap-1.5 text-neutral-400">Propulsé par <VisionLogo variant="light" size={18} /></span>
          </div>
        </footer>
      </div>
    </CartProvider>
  );
}

export default function StorefrontApp() { return <StorefrontInner />; }
